import type { WidgetDataSourceWidget } from './types';

// -- type definitions --

export type CoupledWidgetKind = 'windrose' | 'ombrothermic';

export interface CoupledWidgetDimensionConfig {
  readonly groupKey: string;
  readonly dimensionOrder: readonly string[];
}

export type IndependentDimensionWidget = Extract<
  WidgetDataSourceWidget,
  | 'scatter'
  | 'timeseries'
  | 'histogram'
  | 'aggregation_table'
  | 'table_by_campaign'
>;

export type EntityScopeWidget = Extract<
  WidgetDataSourceWidget,
  'map' | 'text_from_entity_property' | 'image' | 'image_gallery'
>;

// -- slot maps --

const INDEPENDENT_DIMENSION_SLOTS: Record<
  IndependentDimensionWidget,
  readonly string[]
> = {
  scatter: ['unit_x', 'unit_y'],
  timeseries: ['unique'],
  histogram: ['data_series'],
  aggregation_table: ['columns'],
  table_by_campaign: ['data_series'],
};

const COUPLED_DIMENSION_SLOTS: Record<
  CoupledWidgetKind,
  CoupledWidgetDimensionConfig
> = {
  windrose: {
    groupKey: 'wind_data',
    dimensionOrder: ['direction', 'speed'],
  },
  ombrothermic: {
    groupKey: 'climate_data',
    dimensionOrder: ['pluviometry', 'temperature'],
  },
};

/**
 * Returns independent dimension slot names for a widget kind.
 */
export function getDefaultIndependentDimensionKeys(
  widget: WidgetDataSourceWidget
): readonly string[] {
  if (widget in INDEPENDENT_DIMENSION_SLOTS) {
    return INDEPENDENT_DIMENSION_SLOTS[widget as IndependentDimensionWidget];
  }

  return [];
}

/**
 * Returns coupled-group slot metadata for windrose or ombrothermic widgets.
 */
export function getCoupledWidgetDimensionConfig(
  widget: CoupledWidgetKind
): CoupledWidgetDimensionConfig {
  return COUPLED_DIMENSION_SLOTS[widget];
}

/**
 * Returns whether a widget kind selects scoped project-site entities.
 */
export function isEntityScopeWidget(
  widget: WidgetDataSourceWidget
): widget is EntityScopeWidget {
  return (
    widget === 'map' ||
    widget === 'text_from_entity_property' ||
    widget === 'image' ||
    widget === 'image_gallery'
  );
}

/**
 * Flattens an independent-dimension selection map in slot order.
 */
export function flattenIndependentDimensionSources<T>(
  selection: Readonly<Record<string, readonly T[]>>,
  dimensionOrder: readonly string[]
): T[] {
  return dimensionOrder.flatMap((dimensionKey) => selection[dimensionKey] ?? []);
}
